
import { Link } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { 
  LayoutDashboard, 
  Users, 
  Lightbulb, 
  Settings, 
  MessageSquare, 
  Mic,
  CreditCard,
  GraduationCap,
  BookOpen,
  LogOut,
  Mail,
  UserSearch,
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const mainMenuItems = [
  { title: 'Dashboard', icon: LayoutDashboard, url: '/dashboard' },
  { title: 'Team Analysis', icon: Users, url: '/team-analysis' },
  { title: 'Coaching Insights', icon: Lightbulb, url: '/coaching' },
  { title: 'Onboarding', icon: GraduationCap, url: '/onboarding' },
];

const toolsMenuItems = [
  { title: 'Call Analyzers', icon: MessageSquare, url: '/' },
  { title: 'AI Role Play', icon: Mic, url: '/ai-role-play' },
  { title: 'AI Chat', icon: MessageSquare, url: '/ai-chat' },
  { title: 'Voice', icon: Mic, url: '/voice' },
  { title: 'Personalize Outreach', icon: Mail, url: '/personalize-outreach' },
  { title: 'Email Sequence', icon: Mail, url: '/email-sequence' },
];

const prospectMenuItems = [
  { title: 'People', icon: UserSearch, url: '/people' },
];

const crmMenuItems = [
  { title: 'Contacts', icon: Users, url: '/crm/contacts' },
  { title: 'Pipeline', icon: LayoutDashboard, url: '/crm/pipeline' },
];

const settingsMenuItems = [
  { title: 'Usage & Billing', icon: CreditCard, url: '/billing' },
  { title: 'Knowledge Base', icon: BookOpen, url: '/knowledge-base' },
  { title: 'Settings', icon: Settings, url: '/settings' },
];

export function AppSidebar() {
  const isMobile = useIsMobile();
  const { open } = useSidebar();

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('Sign out error:', error);
        toast.error('Error signing out');
      } else {
        toast.success('Signed out successfully');
      }
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Error signing out');
    }
  };

  return (
    <Sidebar className={cn(
      "bg-[#1A1F2C]/95 backdrop-blur-sm border-r border-white/10",
      isMobile && "fixed inset-0 z-50"
    )}>
      <SidebarContent>
        <div className="px-4 py-6 flex items-center justify-between">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            ERP CoPilot AI
          </h1>
        </div>
        
        {/* Trigger button - only visible when sidebar is closed */}
        {!open && (
          <SidebarTrigger className="fixed left-0 top-4 z-50 bg-primary/90 hover:bg-primary backdrop-blur-sm border-r border-white/20 text-white rounded-r-lg p-3 transition-all duration-200 shadow-lg" />
        )}

        <SidebarGroup>
          <SidebarGroupLabel className="text-gray-400">DATA-DRIVEN SALES</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url} className="flex items-center gap-3 text-gray-300 hover:text-white">
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-gray-400">TOOLS & AI</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {toolsMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url} className="flex items-center gap-3 text-gray-300 hover:text-white">
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <SidebarGroup>
          <SidebarGroupLabel className="text-gray-400">FIND PROSPECTS</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {prospectMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url} className="flex items-center gap-3 text-gray-300 hover:text-white">
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-400">CRM</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {crmMenuItems.map((item) => (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton asChild>
                      <Link to={item.url} className="flex items-center gap-3 text-gray-300 hover:text-white">
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarGroup>
            <SidebarGroupLabel className="text-gray-400">SETTINGS</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <Link to={item.url} className="flex items-center gap-3 text-gray-300 hover:text-white">
                      <item.icon className="h-4 w-4" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              <SidebarMenuItem>
                <SidebarMenuButton onClick={handleSignOut} className="flex items-center gap-3 text-gray-300 hover:text-white w-full">
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
